import React, { useMemo } from "react";
import { Box, Typography, Link, useTheme } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ComputerIcon from "@mui/icons-material/Computer";
import LanguageIcon from "@mui/icons-material/Language";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import PhotoSizeSelectActualIcon from "@mui/icons-material/PhotoSizeSelectActual";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";

interface InfoTabProps {
  url?: string;
  timestamp?: string | Date;
  os?: string | object;
  browser?: string | object;
  windowSize?: string | { resolution?: string; windowSize?: string; colorDepth?: string; pixelRatio?: string; orientation?: string };
  country?: string;
  screenshotDimensions?: string;
  timezone?: string;
}

// Helper to safely extract string value from props that might be objects
const getStringValue = (value: any, fallbackKey?: string): string => {
  if (!value) return "-";
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // Try common keys
    if (fallbackKey && value[fallbackKey]) return String(value[fallbackKey]);
    if (value.windowSize) return String(value.windowSize);
    if (value.resolution) return String(value.resolution);
    if (value.name) return String(value.name);
    if (value.value) return String(value.value);
    // Last resort: stringify the object
    return JSON.stringify(value);
  }
  return String(value);
};

const InfoTab: React.FC<InfoTabProps> = ({
  url,
  timestamp,
  os,
  browser,
  windowSize,
  country,
  screenshotDimensions,
  timezone,
}) => {
  const theme = useTheme();
  const isPublicBugPage = window?.location?.pathname.includes('public/bug');

  // Theme colors based on public/private context
  const themeColors = useMemo(() => {
    if (isPublicBugPage) {
      return {
        containerBg: "#ffffff",
        labelColor: "#666666",
        valueColor: "#333333",
        linkColor: "#1976d2",
        borderColor: "#f0f0f0",
        iconColor: "#666666",
      };
    }
    return {
      containerBg: theme.palette.background.paper,
      labelColor: theme.palette.text.secondary,
      valueColor: theme.palette.text.primary,
      linkColor: theme.palette.primary.main,
      borderColor: theme.palette.divider,
      iconColor: theme.palette.text.secondary,
    };
  }, [isPublicBugPage, theme]);
  const formatTimestamp = (ts: string | Date | undefined) => {
    if (!ts) return "-";
    try {
      const date = typeof ts === "string" ? new Date(ts) : ts;
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
      };
      return date.toLocaleString("en-US", options);
    } catch {
      return String(ts);
    }
  };

  const InfoRow = ({
    label,
    value,
    icon,
    isLink = false,
  }: {
    label: string;
    value: string;
    icon?: React.ReactNode;
    isLink?: boolean;
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        py: 1.5,
        borderBottom: `1px solid ${themeColors.borderColor}`,
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        sx={{
          minWidth: "180px",
          color: themeColors.labelColor,
          fontSize: "13px",
          fontWeight: 400,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
        {isLink && value !== "-" ? (
          <Link
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: themeColors.linkColor,
              fontSize: "13px",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {value}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        ) : (
          <Typography
            sx={{
              color: themeColors.valueColor,
              fontSize: "13px",
              fontWeight: 400,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {value || "-"}
            {icon}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // Get browser icon based on browser name
  const getBrowserIcon = () => {
    const browserStr = getStringValue(browser);
    if (browserStr === "-") return null;
    const browserLower = browserStr.toLowerCase();
    if (browserLower.includes("chrome")) {
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            width: 16,
            height: 16,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, #ea4335 25%, #fbbc05 25%, #fbbc05 50%, #34a853 50%, #34a853 75%, #4285f4 75%)",
          }}
        />
      );
    }
    if (browserLower.includes("firefox")) {
      return <LanguageIcon sx={{ fontSize: 16, color: "#ff7139" }} />;
    }
    if (browserLower.includes("safari")) {
      return <LanguageIcon sx={{ fontSize: 16, color: "#006cff" }} />;
    }
    if (browserLower.includes("edge")) {
      return <LanguageIcon sx={{ fontSize: 16, color: "#0078d7" }} />;
    }
    return <LanguageIcon sx={{ fontSize: 16 }} />;
  };

  // Get OS icon
  const getOSIcon = () => {
    const osStr = getStringValue(os);
    if (osStr === "-") return null;
    const osLower = osStr.toLowerCase();
    if (osLower.includes("windows")) {
      return <ComputerIcon sx={{ fontSize: 16, color: "#0078d4" }} />;
    }
    if (osLower.includes("mac")) {
      return <ComputerIcon sx={{ fontSize: 16, color: "#555" }} />;
    }
    if (osLower.includes("linux")) {
      return <ComputerIcon sx={{ fontSize: 16, color: "#f5a623" }} />;
    }
    return <ComputerIcon sx={{ fontSize: 16 }} />;
  };

  return (
    <Box sx={{ p: 2, bgcolor: themeColors.containerBg, height: "100%", overflow: "auto" }}>
      <InfoRow label="URL" value={url || "-"} isLink={!!url} />
      <InfoRow
        label="Timestamp"
        value={formatTimestamp(timestamp)}
        icon={<AccessTimeIcon sx={{ fontSize: 14, color: "#666", ml: 0.5 }} />}
      />
      <InfoRow label="OS" value={getStringValue(os)} icon={getOSIcon()} />
      <InfoRow label="Browser" value={getStringValue(browser)} icon={getBrowserIcon()} />
      <InfoRow
        label="Window size"
        value={getStringValue(windowSize, 'windowSize')}
        icon={<AspectRatioIcon sx={{ fontSize: 14, color: "#666", ml: 0.5 }} />}
      />
      {country && (
        <InfoRow
          label="Country"
          value={getStringValue(country)}
          icon={<PublicIcon sx={{ fontSize: 14, color: "#666", ml: 0.5 }} />}
        />
      )}
      {timezone && !country && (
        <InfoRow
          label="Timezone"
          value={getStringValue(timezone)}
          icon={<PublicIcon sx={{ fontSize: 14, color: "#666", ml: 0.5 }} />}
        />
      )}
      <InfoRow
        label="Screenshot dimensions"
        value={getStringValue(screenshotDimensions)}
        icon={
          <PhotoSizeSelectActualIcon
            sx={{ fontSize: 14, color: "#666", ml: 0.5 }}
          />
        }
      />
    </Box>
  );
};

export default InfoTab;
